<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">{{ $t("SUBSCRIPTIONS.SUBSCRIPTIONS_LIST") }}</h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            @click="addSubscription()"
            v-if="$currentUserCan($permissions.PERM_CREATE_SUBSCRIPTIONS)"
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("SUBSCRIPTIONS.ADD_SUBSCRIPTION") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>
    <div>
      <div
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <package-selector
          v-if="
            !filterPackage &&
            $currentUserCan($permissions.PERM_VIEW_ANY_PACKAGES)
          "
          @packageChanged="(packageId) => (selectedPackage = packageId)"
        />

        <base-input placeholder="Dates">
          <flat-picker
            :config="{
              allowInput: true,
              mode: 'range',
              locale: flatPickrLocale,
            }"
            class="form-control datepicker"
            v-model="startDateRange"
            :placeholder="$t('SUBSCRIPTIONS.START_TIME')"
          >
          </flat-picker>
        </base-input>

        <base-input placeholder="Dates">
          <flat-picker
            :config="{
              allowInput: true,
              mode: 'range',
              locale: flatPickrLocale,
            }"
            class="form-control datepicker"
            v-model="endDateRange"
            :placeholder="$t('SUBSCRIPTIONS.END_TIME')"
          >
          </flat-picker>
        </base-input>

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.CODE')"
            clearable
          />
        </div>
      </div>
      <el-table
        class="table-responsive align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="subscriptions"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>
        <el-table-column width="60px">
          <template v-slot="{ row }">
            <span
              v-if="isSubscriptionExpired(row)"
              class="text-danger text-bold"
            >
              <i class="ni ni-fat-remove" />
            </span>
            <span
              v-if="!isSubscriptionExpired(row)"
              class="text-success text-bold"
            >
              <i class="ni ni-check-bold" />
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.CODE')"
          prop="code"
          sortable="custom"
        />

        <el-table-column
          sortable="custom"
          :label="$t('SUBSCRIPTIONS.START_TIME')"
          prop="start_time"
        />

        <el-table-column
          sortable="custom"
          :label="$t('SUBSCRIPTIONS.END_TIME')"
          prop="end_time"
        />

        <el-table-column
          :label="$t('COMMON.PACKAGE')"
          v-if="
            !filterPackage &&
            $currentUserCan($permissions.PERM_VIEW_ANY_PACKAGES)
          "
        >
          <template v-slot="{ row }">
            <span>
              {{ row.package ? row.package.name : null }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.ORGANIZATION')"
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.CREATED_AT')"
          prop="created_at"
          sortable="custom"
        >
          <template v-slot="{ row }">
            {{ $formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column min-width="50px" align="center">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_SUBSCRIPTIONS)"
            >
              <a
                type="text"
                @click="viewSubscription(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_SUBSCRIPTIONS)"
            >
              <a
                type="text"
                @click="editSubscription(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              content="Delete"
              placement="top"
              v-if="
                $currentUserCan($permissions.PERM_DELETE_SUBSCRIPTIONS) && false
              "
            >
              <a
                type="text"
                @click="deleteSubscription(row.id)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import moment from "moment";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import flatPicker from "vue-flatpickr-component";
import { French } from "flatpickr/dist/l10n/fr.js";
import "flatpickr/dist/flatpickr.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import PackageSelector from "@/components/PackageSelector.vue";

export default {
  name: "subscription-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    PackageSelector,
    flatPicker,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterPackage: {
      type: String,
      default: null,
      description: "Package id",
    },
  },

  data() {
    return {
      flatPickrLocale: French,
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      subscriptions: [],
      loading: true,
      selectedOrganization: null,
      selectedPackage: null,
      startDateRange: null,
      endDateRange: null,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterPackage: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedPackage: {
      handler: "getListDebounced",
      immediate: true,
    },
    startDateRange: {
      handler: "getListDebounced",
      immediate: false,
      deep: true,
    },
    endDateRange: {
      handler: "getListDebounced",
      immediate: false,
      deep: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        const vStartDateRange = this.startDateRange
          ? this.startDateRange.split(this.flatPickrLocale.rangeSeparator)
          : [];
        const vEndDateRange = this.endDateRange
          ? this.endDateRange.split(this.flatPickrLocale.rangeSeparator)
          : [];
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { code: this.query } : {}),
            ...(vStartDateRange.length == 2
              ? {
                  start_time: [
                    vStartDateRange[0] + " 00:00:00",
                    vStartDateRange[1] + " 23:59:59",
                  ],
                }
              : {}),
            ...(vEndDateRange.length == 2
              ? {
                  end_time: [
                    vEndDateRange[0] + " 00:00:00",
                    vEndDateRange[1] + " 23:59:59",
                  ],
                }
              : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,package",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }

        if (this.filterPackage) {
          params = {
            ...params,
            filter: { ...params.filter, package: this.filterPackage },
          };
        }
        if (this.selectedPackage) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              package: this.selectedPackage,
            },
          };
        }

        await this.$store.dispatch("subscriptions/list", params);
        this.subscriptions = this.$store.getters["subscriptions/list"];
        this.total = this.$store.getters["subscriptions/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteSubscription(id) {
      const confirmation = await swal.fire({
        title: this.$t("SUBSCRIPTIONS.DELETE_THIS_SUBSCRIPTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("subscriptions/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t("SUBSCRIPTIONS.SUBSCRIPTION_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    isSubscriptionExpired(subscription) {
      return moment(subscription.end_time) < moment();
    },

    viewSubscription(subscription) {
      this.$router.push({
        name: "View Subscription",
        params: { id: subscription.id },
      });
    },

    async editSubscription(row) {
      this.$router.push({
        name: "Edit Subscription",
        params: { id: row.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
